import { UserRole } from "../types/user.type";

export const hasAdminAccess = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin";
};

export const hasDirectorAccess = (role?: UserRole | string): boolean => {
  return role === UserRole.Director || role === "director";
};

export const hasAdminOrDirectorAccess = (role?: UserRole | string): boolean => {
  return hasAdminAccess(role) || hasDirectorAccess(role);
};

export const canEdit = (role?: UserRole | string): boolean => {
  return hasAdminAccess(role);
};

export const canView = (role?: UserRole | string): boolean => {
  return hasAdminOrDirectorAccess(role);
};

export const canAddFiles = (role?: UserRole | string): boolean => {
  return hasAdminOrDirectorAccess(role);
};

// Entity-specific permissions (based on backend routes)

// Positions - admin, cashier, director can create/edit
export const canViewPositions = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Director || role === "director";
};

export const canEditPositions = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Director || role === "director";
};

// Machines - admin, manager, accountant, cashier, director can create/edit
export const canEditMachines = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Manager || role === "manager" ||
         role === UserRole.Accountant || role === "accountant" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Director || role === "director";
};

// Materials - admin, manager, director can create/edit  
export const canEditMaterials = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Manager || role === "manager" ||
         role === UserRole.Director || role === "director";
};

// Produces - admin, director can create/edit
export const canEditProduces = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Director || role === "director";
};

// Expenses - admin, cashier, director can create/edit/delete
export const canEditExpenses = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Director || role === "director";
};

// Lists - admin, foreman, accountant, director can create/edit
export const canEditLists = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Foreman || role === "foreman" ||
         role === UserRole.Accountant || role === "accountant" ||
         role === UserRole.Director || role === "director";
};

// Fillers - admin, cashier, foreman, director can create/edit
export const canEditFillers = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Foreman || role === "foreman" ||
         role === UserRole.Director || role === "director";
};

// Inventory - admin, cashier, inventoryMgr, director can create/edit
export const canEditInventory = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.InventoryMgr || role === "inventoryMgr" ||
         role === UserRole.Director || role === "director";
};

// Workers - admin, accountant, cashier, director can create/edit
export const canEditWorkers = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Accountant || role === "accountant" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Director || role === "director";
};

// Clients - admin, cashier, manager, salesDept, director can create/edit
export const canEditClients = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Manager || role === "manager" ||
         role === UserRole.SalesDept || role === "salesDept" ||
         role === UserRole.Director || role === "director";
};

// Clients - only admin can delete/restore
export const canDeleteClients = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin";
};

// Products - admin, cashier, manager, salesDept, director can create/edit
export const canEditProducts = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Manager || role === "manager" ||
         role === UserRole.SalesDept || role === "salesDept" ||
         role === UserRole.Director || role === "director";
};

// Partners - admin, cashier, director can create/edit
export const canEditPartners = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Director || role === "director";
};

// Cash Transfers - admin, cashier, director can create/edit
export const canEditCashTransfers = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Director || role === "director";
};

// Orders - admin, cashier, manager, director can create/edit
export const canEditOrders = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Manager || role === "manager" ||
         role === UserRole.Director || role === "director";
};

// Meter Readings - admin, cashier, director can create/edit meter readings themselves
export const canEditMeterReadings = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Director || role === "director";
};

// Reports - admin, cashier, director can create reports
export const canEditReports = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Director || role === "director";
};

// Task Order Reports - admin, salesDept, manager, director can create
export const canEditTaskOrderReports = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.SalesDept || role === "salesDept" ||
         role === UserRole.Manager || role === "manager" ||
         role === UserRole.Director || role === "director";
};

// Spool payout (выкуп катушки) - денежная операция: admin, cashier, director
export const canPaySpools = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Cashier || role === "cashier" ||
         role === UserRole.Director || role === "director";
};

// Staff/Users - admin, director can create/edit
export const canEditStaff = (role?: UserRole | string): boolean => {
  return role === UserRole.Admin || role === "admin" ||
         role === UserRole.Director || role === "director";
}; 